import React from "react";
import { useField } from "formik";
import {
    copySplitLineAssignments,
    splitRuleShowsAutoConfirm,
} from "@common/helpers/splitRuleAction";
import { Checkbox } from "@design-system/Checkbox/Checkbox";
import { HStack } from "@design-system/HStack/HStack";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { TransactionRuleActionFormSchema } from "../constants";
import actionStyles from "./ActionForm.module.scss";
import styles from "./RuleSplitForm.module.scss";

interface Props {
    index: number;
}

const getDefaultEqualSplitPercentage = (
    currentIndex: number,
    totalSplits: number,
): number => {
    const percentage = 100 / totalSplits;

    if (currentIndex < totalSplits - 1) {
        return Math.round(percentage * 100) / 100;
    }

    const total = (Math.round(percentage * 100) / 100) * (totalSplits - 1);
    return 100 - total;
};

export const SplitRuleTypeForm: React.FC<Props> = ({ index }) => {
    const [field, , helpers] = useField<
        TransactionRuleActionFormSchema["value"]
    >(`actions.${index}.value`);

    const showAutoConfirm = splitRuleShowsAutoConfirm(
        field.value?.splitAction?.splits ?? [],
    );

    return (
        <HStack gap={16} align="center">
            {showAutoConfirm && (
                <Tooltip content="Automatically post transactions that meet rule conditions to the accrual ledger">
                    <span className={actionStyles.autoConfirmCheckbox}>
                        <Checkbox
                            checked={!!field.value?.splitAction?.autoConfirm}
                            label={
                                <span className={actionStyles.autoConfirmLabel}>
                                    Auto confirm
                                </span>
                            }
                            onCheckedChange={(checked) =>
                                helpers.setValue({
                                    ...field.value,
                                    splitAction: {
                                        ...field.value?.splitAction,
                                        autoConfirm: checked,
                                        splits:
                                            field.value?.splitAction?.splits ??
                                            [],
                                    },
                                })
                            }
                        />
                    </span>
                </Tooltip>
            )}
            <Checkbox
                checked={field.value?.splitAction?.isPercentage !== true}
                label={
                    <span className={styles.splitRuleTypeFormLabel}>
                        Split by amount
                    </span>
                }
                onCheckedChange={() => {
                    const changingToPercentageBased =
                        !field.value?.splitAction?.isPercentage;

                    helpers.setValue({
                        ...field.value,
                        splitAction: {
                            ...field.value?.splitAction,
                            isPercentage: changingToPercentageBased,
                            splits: (
                                field.value?.splitAction?.splits ?? []
                            ).map((split, splitIndex, splits) => {
                                const percentageValue =
                                    getDefaultEqualSplitPercentage(
                                        splitIndex,
                                        splits.length,
                                    );
                                const amountValue =
                                    splitIndex !== splits.length - 1
                                        ? 0
                                        : undefined;

                                return {
                                    ...copySplitLineAssignments(split),
                                    ...(changingToPercentageBased
                                        ? {
                                              percentageAmount: percentageValue,
                                          }
                                        : {
                                              amount: amountValue,
                                          }),
                                };
                            }),
                        },
                    });
                }}
            />
        </HStack>
    );
};
